import { useTranslation } from "react-i18next"

/**
 * Pull sheet statuses in the order the backend moves them (constants/PULL_SHEET_STATUS.ts,
 * pull-sheet/complete.ts, confirm.ts): APPROVED -> LOADING -> CONFIRMED (loader finished) -> LOADED
 * (warehouse keeper confirmed). One list, one set of names (`delivery:psStatus.*`) for the list,
 * the filter, the detail stepper and the shortage selector.
 */
export const PULL_SHEET_FLOW = ["APPROVED", "LOADING", "CONFIRMED", "LOADED"] as const

export type PullSheetStatusCode = (typeof PULL_SHEET_FLOW)[number]

export const PULL_SHEET_COLOR: Record<PullSheetStatusCode, string> = {
  APPROVED: "var(--mantine-color-gray-6)",
  LOADING: "var(--mantine-color-orange-6)",
  CONFIRMED: "var(--mantine-color-blue-6)",
  LOADED: "var(--mantine-color-green-7)",
}

/** Statuses that have a printable sheet (pull-sheet/pdf.ts is only used from CONFIRMED on). */
export const PULL_SHEET_PDF_STATUSES: string[] = ["CONFIRMED", "LOADED"]

export function isPullSheetStatus(code: string | null | undefined): code is PullSheetStatusCode {
  return !!code && (PULL_SHEET_FLOW as readonly string[]).includes(code)
}

export function usePullSheetStatus() {
  const { t } = useTranslation("delivery")
  return {
    nameOf: (code: string | null | undefined) => (isPullSheetStatus(code) ? t(`psStatus.${code}`) : "-"),
    colorOf: (code: string | null | undefined) => (isPullSheetStatus(code) ? PULL_SHEET_COLOR[code] : "var(--mantine-color-gray-6)"),
    options: PULL_SHEET_FLOW.map((code) => ({ value: code, label: t(`psStatus.${code}`) })),
  }
}
